import { type User, usersEndpoints } from "../../../api";
import { type StrapiFilters, type StrapiSort, useStrapiList } from "../../../hooks/strapi";
import handleError from "../../utils/handleError";

interface UseUsersProps {
  filters?: StrapiFilters;
  autoLoad?: boolean;
  sort?: StrapiSort;
  pagination?: { page: number; pageSize: number };
  populate?: string | string[];
}

/**
 * Transform user-friendly filter keys to Strapi filter format.
 * Called automatically before each API request.
 */
const transformFilters = (filters: StrapiFilters): StrapiFilters | undefined => {
  if (!filters || Object.keys(filters).length === 0) return undefined;

  // Type for Strapi $and array conditions
  const andConditions: Record<string, unknown>[] = [
    {
      $or: [{ isTest: { $eq: false } }, { isTest: { $null: true } }],
    },
  ];

  const result: StrapiFilters = {
    // Exclude test users by default
    $and: andConditions,
  };

  for (const [key, val] of Object.entries(filters)) {
    if (val === undefined || val === null || val === "") continue;

    switch (key) {
      case "search":
        andConditions.push({
          $or: [
            { email: { $containsi: val } },
            { name: { $containsi: val } },
            { exchange_accounts: { accountId: { $containsi: val } } },
          ],
        });
        break;
      case "isPartner":
        if (typeof val === "boolean") {
          result.isPartner = val;
        }
        break;
      default:
        result[key] = val;
    }
  }

  return result;
};

function getErrorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "object" && e !== null && "message" in e) {
    const errorWithMessage = e;
    if (typeof errorWithMessage.message === "string") return errorWithMessage.message;
  }
  return "Error al cargar usuarios";
}

// autoLoad, sort, pagination, filters, populate
const useUsers = (props?: UseUsersProps) => {
  const {
    items: users,
    refresh,
    isLoading,
    ...other
  } = useStrapiList<User>({
    ...props,
    transformFilters,
    load: loadUsers,
    onLoadError: (e: unknown) => handleError(getErrorMessage(e)),
  });

  return {
    users,
    refresh,
    isLoading,
    ...other,
  };
};

export const loadUsers = async (params?: Record<string, unknown>) => {
  try {
    const res = await usersEndpoints.getUsers(params);
    // res is { data, meta } - return in format expected by useStrapiList
    return { data: res.data, meta: res.meta || { pagination: null } };
  } catch (e: unknown) {
    handleError(getErrorMessage(e));
  }
};

export default useUsers;
